import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ComplianceSubPageHeader } from '@/features/compliance-studio/machinery-directive/components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '@/features/compliance-studio/machinery-directive/components/ComplianceSubPageTitle'

const OVERVIEW_STEPS = [
  {
    step: 1,
    path: 'risk-assessment',
    titleKey: 'nav.riskAssessment',
    descriptionKey: 'overview.steps.riskAssessment',
  },
  {
    step: 2,
    path: 'user-information',
    titleKey: 'nav.userInformation',
    descriptionKey: 'overview.steps.userInformation',
  },
  {
    step: 3,
    path: 'declaration-of-conformity',
    titleKey: 'nav.declarationOfConformity',
    descriptionKey: 'overview.steps.declarationOfConformity',
  },
  {
    step: 4,
    path: 'technical-documentation',
    titleKey: 'nav.technicalDocumentation',
    descriptionKey: 'overview.steps.technicalDocumentation',
  },
] as const

function OverviewStep({
  step,
  title,
  description,
  actionHref,
  actionLabel,
}: {
  step: number
  title: string
  description: string
  actionHref: string
  actionLabel: string
}) {
  return (
    <div className='flex gap-3'>
      <div className='flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 font-semibold text-primary'>
        {step}
      </div>
      <div className='min-w-0 flex-1'>
        <p className='mb-1 text-sm font-medium text-foreground'>{title}</p>
        <p className='text-sm text-muted-foreground'>{description}</p>
        <Button variant='outline' size='sm' className='mt-2' asChild>
          <Link to={actionHref}>{actionLabel}</Link>
        </Button>
      </div>
    </div>
  )
}

export function CraIntroductionPage() {
  const { t } = useTranslation('craCompliance')
  const { productId = '', craStudioId = '' } = useParams()
  const base = `/product-catalog/${productId}/cra/${craStudioId}`

  return (
    <div className='space-y-6'>
      <ComplianceSubPageHeader>
        <div>
          <ComplianceSubPageTitle>
            {t('cyberResilienceAct.title')}
          </ComplianceSubPageTitle>
          <p className='text-lg text-muted-foreground'>
            {t('cyberResilienceAct.description')}
          </p>
        </div>
      </ComplianceSubPageHeader>
      <Card className='border-border bg-card'>
        <CardHeader>
          <CardTitle>{t('cyberResilienceAct.instructions.title')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          {OVERVIEW_STEPS.map((step) => (
            <OverviewStep
              key={step.path}
              step={step.step}
              title={t(step.titleKey)}
              description={t(step.descriptionKey)}
              actionHref={`${base}/${step.path}`}
              actionLabel={t('overview.open')}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
